import React, { useState, useEffect, useCallback } from "react";
import { useApp, apiFetch } from "../../context/AppContext";
import { REGION_COUNTRIES } from "../../constants/complianceRegions";
import {
  Card,
  Badge,
  Button,
  Modal,
  Input,
  Select,
  ProgressBar,
  DashboardStatCard,
  Pagination,
  FormErrorsSummary,
} from "../../components/common/UI";
import {
  Plus,
  Building2,
  Users,
  Database,
  Search,
  Edit2,
  ToggleLeft,
  ToggleRight,
  Package,
  Trash2,
  AlertTriangle,
  Key,
} from "lucide-react";

const PLANS = [
  { value: "trial", label: "Trial" },
  { value: "starter", label: "Starter" },
  { value: "professional", label: "Professional" },
  { value: "enterprise", label: "Enterprise" },
];

const PLAN_DEFAULTS = {
  trial: { user_limit: 5, days: 14 },
  starter: { user_limit: 10, days: 365 },
  professional: { user_limit: 25, days: 365 },
  enterprise: { user_limit: 100, days: 365 },
};

const DOMAIN_RE =
  /^([a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/;

function emailMatchesTenantDomain(email, domain) {
  const e = (email || "").trim().toLowerCase();
  const d = (domain || "").trim().toLowerCase();
  if (!e || !d || !e.includes("@")) return false;
  const emailDomain = e.split("@")[1];
  return emailDomain === d || emailDomain.endsWith(`.${d}`);
}

function adminEmailDomainError(adminEmail, domain) {
  const d = (domain || "").trim().toLowerCase();
  if (!d || !adminEmail || !/\S+@\S+\.\S+/.test(adminEmail)) return null;
  if (!emailMatchesTenantDomain(adminEmail, d)) {
    return `Email must use the tenant domain (@${d})`;
  }
  return null;
}

const planExpiryDate = (days) =>
  new Date(Date.now() + days * 24 * 3600 * 1000).toISOString().split("T")[0];

const MODULE_OPTIONS = [
  { value: "consent", label: "Consent Variance" },
  { value: "dsar", label: "DSAR" },
  { value: "dspm", label: "DSPM" },
  { value: "dpia", label: "DPIA" },
  { value: "gap", label: "Gap Assessment" },
  { value: "tprm", label: "TPRM" },
  { value: "ropa", label: "ROPA" },
  { value: "breach", label: "Breach Management" },
  { value: "policy_store", label: "Policy Store" },
  { value: "reports", label: "Reports" },
  { value: "asset_tracker", label: "Asset Tracker" },
];

const moduleLabel = (value) =>
  MODULE_OPTIONS.find((m) => m.value === value)?.label || value;

const DEFAULT_GEO = { region: "Asia Pacific", country: "India" };

function resolveGeo(region, country) {
  const nextRegion = country === "India" && (region === "India" || region === "Asia Pacific")
    ? "Asia Pacific"
    : region;
  if (nextRegion && (REGION_COUNTRIES[nextRegion] || []).includes(country)) {
    return { region: nextRegion, country };
  }
  return { ...DEFAULT_GEO };
}

function selectionKey(item) {
  return `${item.region}|${item.country}`;
}

function GeoMultiSelect({ selections, onChange, error }) {
  const [region, setRegion] = useState(selections[0]?.region || DEFAULT_GEO.region);
  const countries = REGION_COUNTRIES[region] || [];
  const selected = new Set((selections || []).map(selectionKey));

  const toggle = (country) => {
    const item = { region, country };
    const key = selectionKey(item);
    if (selected.has(key)) {
      const next = selections.filter((row) => selectionKey(row) !== key);
      onChange(next.length ? next : [{ ...DEFAULT_GEO }]);
      return;
    }
    onChange([...selections, item]);
  };

  return (
    <div className="sm:col-span-2 space-y-2">
      <Select label="Region" value={region} onChange={(e) => setRegion(e.target.value)}>
        {Object.keys(REGION_COUNTRIES).map((name) => (
          <option key={name} value={name}>{name}</option>
        ))}
      </Select>
      <div>
        <p className="mb-1.5 text-sm font-medium text-slate-700">Countries</p>
        <div className="grid max-h-48 grid-cols-2 gap-2 overflow-y-auto rounded-lg border border-slate-200 bg-white p-2">
          {countries.map((country) => (
            <label key={`${region}-${country}`} className="flex items-center gap-2 text-sm text-slate-800">
              <input
                type="checkbox"
                className="rounded border-slate-300 text-primary-600 focus:ring-primary-500"
                checked={selected.has(`${region}|${country}`)}
                onChange={() => toggle(country)}
              />
              {country}
            </label>
          ))}
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        {(selections || []).map((item) => (
          <button
            key={selectionKey(item)}
            type="button"
            onClick={() => {
              const next = selections.filter((row) => selectionKey(row) !== selectionKey(item));
              onChange(next.length ? next : [{ ...DEFAULT_GEO }]);
            }}
            className="rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-semibold text-slate-900 hover:border-red-200 hover:text-red-700"
          >
            {item.country} ×
          </button>
        ))}
      </div>
      {error && <p className="text-xs font-medium text-red-700">{error}</p>}
    </div>
  );
}

function ModulePicker({ value = [], onChange }) {
  const toggle = (mod) => {
    onChange(
      value.includes(mod) ? value.filter((m) => m !== mod) : [...value, mod],
    );
  };

  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
      {MODULE_OPTIONS.map((opt) => {
        const checked = value.includes(opt.value);
        return (
          <label
            key={opt.value}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm cursor-pointer transition-colors ${
              checked
                ? "border-primary-300 bg-primary-50 text-primary-800"
                : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
            }`}
          >
            <input
              type="checkbox"
              className="rounded border-slate-300 text-primary-600 focus:ring-primary-500"
              checked={checked}
              onChange={() => toggle(opt.value)}
            />
            <span className="font-medium">{opt.label}</span>
          </label>
        );
      })}
    </div>
  );
}

const EMPTY_FORM = {
  company_name: "",
  domain_name: "",
  organization_id: "",
  contact_email: "",
  region: DEFAULT_GEO.region,
  country: DEFAULT_GEO.country,
  selections: [{ ...DEFAULT_GEO }],
  admin_full_name: "",
  admin_email: "",
  admin_password: "",
  license_plan: "starter",
  user_limit: 10,
  license_expires_at: planExpiryDate(365),
  modules: [],
};

export default function TenantsPage() {
  const { showNotification } = useApp();

  const [tenants, setTenants] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [showCreate, setShowCreate] = useState(false);
  const [showEdit, setShowEdit] = useState(null); // holds the tenant object being edited
  const [deleteTarget, setDeleteTarget] = useState(null); // tenant pending deletion confirmation
  const [deleting, setDeleting] = useState(false);
  const [adminResetPassword, setAdminResetPassword] = useState("");
  const [resettingAdminPassword, setResettingAdminPassword] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});

  // ── Fetch tenants ─────────────────────────────────────────────────────
  const fetchTenants = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, page_size: 20 });
      if (search) params.append("search", search);
      const res = await apiFetch(`/admin/tenants?${params}`);
      if (!res.ok) {
        showNotification("Failed to load tenants", "error");
        return;
      }
      const data = await res.json();
      setTenants(data.items || []);
      setTotal(data.total || 0);
      setPages(data.pages || 1);
    } catch {
      showNotification("Network error", "error");
    } finally {
      setLoading(false);
    }
  }, [page, search, showNotification]);

  useEffect(() => {
    fetchTenants();
  }, [fetchTenants]);

  // ── Debounce search ───────────────────────────────────────────────────
  useEffect(() => {
    const t = setTimeout(() => {
      setPage(1);
      fetchTenants();
    }, 400);
    return () => clearTimeout(t);
  }, [search]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Validate create form ──────────────────────────────────────────────
  const handlePlanChange = (plan) => {
    const defaults = PLAN_DEFAULTS[plan] || PLAN_DEFAULTS.starter;
    setForm((f) => ({
      ...f,
      license_plan: plan,
      user_limit: defaults.user_limit,
      license_expires_at: planExpiryDate(defaults.days),
    }));
  };

  const validate = () => {
    const e = {};
    if (!form.company_name.trim()) e.company_name = "Company name is required";
    const domain = form.domain_name.trim().toLowerCase();
    if (!domain) {
      e.domain_name = "Domain name is required";
    } else if (!DOMAIN_RE.test(domain)) {
      e.domain_name = "Enter a valid domain (e.g. acme.com)";
    }
    const orgId = parseInt(form.organization_id, 10);
    if (!form.organization_id || Number.isNaN(orgId) || orgId < 1) {
      e.organization_id = "Organization ID is required (positive integer)";
    }
    if (!/\S+@\S+\.\S+/.test(form.contact_email))
      e.contact_email = "Valid email required";
    if (!form.selections?.length) e.country = "Select at least one country";
    if (!form.admin_full_name.trim())
      e.admin_full_name = "Admin name is required";
    if (!/\S+@\S+\.\S+/.test(form.admin_email)) {
      e.admin_email = "Valid admin email required";
    } else if (domain) {
      const domainErr = adminEmailDomainError(form.admin_email, domain);
      if (domainErr) e.admin_email = domainErr;
    }
    if (!form.admin_password || form.admin_password.length < 8)
      e.admin_password = "Min 8 characters";
    if (!form.user_limit || form.user_limit < 1)
      e.user_limit = "At least 1 user";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  // ── Create tenant ─────────────────────────────────────────────────────
  const handleCreate = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const res = await apiFetch("/admin/tenants", {
        method: "POST",
        body: JSON.stringify({
          ...form,
          domain_name: form.domain_name.trim().toLowerCase(),
          organization_id: parseInt(form.organization_id, 10),
          user_limit: parseInt(form.user_limit, 10),
          selections: form.selections,
          region: form.selections[0]?.region || DEFAULT_GEO.region,
          country: form.selections[0]?.country || DEFAULT_GEO.country,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        showNotification(data.detail || "Failed to create tenant", "error");
        return;
      }
      showNotification(
        `Tenant "${data.company_name}" created (Org ID: ${data.organization_id ?? "—"})`,
      );
      setShowCreate(false);
      setForm(EMPTY_FORM);
      setErrors({});
      fetchTenants();
    } catch {
      showNotification("Network error", "error");
    } finally {
      setSaving(false);
    }
  };

  // ── Update tenant (modules + license) ─────────────────────────────────
  const openEditTenant = async (ta) => {
    try {
      const res = await apiFetch(`/admin/tenants/${ta.id}`);
      if (!res.ok) {
        showNotification("Failed to load tenant details", "error");
        return;
      }
      const data = await res.json();
      const selections = (data.selections || []).length
        ? data.selections
        : [resolveGeo(data.region, data.country)];
      setShowEdit({
        ...data,
        ...resolveGeo(data.region, data.country),
        selections,
        modules: [...(data.modules || [])],
        license: data.license || ta.license,
      });
    } catch {
      showNotification("Network error", "error");
    }
  };

  const handleSaveEdit = async () => {
    if (!showEdit) return;
    if (!showEdit.company_name?.trim()) {
      showNotification("Company name is required", "error");
      return;
    }
    if (!/\S+@\S+\.\S+/.test(showEdit.contact_email || "")) {
      showNotification("Valid contact email is required", "error");
      return;
    }
    if (!showEdit.selections?.length) {
      showNotification("Select at least one country", "error");
      return;
    }
    setSaving(true);
    try {
      const [tenantRes, licenseRes] = await Promise.all([
        apiFetch(`/admin/tenants/${showEdit.id}`, {
          method: "PATCH",
          body: JSON.stringify({
            company_name: showEdit.company_name.trim(),
            contact_email: showEdit.contact_email,
            region: showEdit.selections[0]?.region || DEFAULT_GEO.region,
            country: showEdit.selections[0]?.country || DEFAULT_GEO.country,
            selections: showEdit.selections,
            notes: showEdit.notes?.trim() || null,
            modules: showEdit.modules || [],
          }),
        }),
        apiFetch(`/admin/tenants/${showEdit.id}/license`, {
          method: "PATCH",
          body: JSON.stringify({
            plan: showEdit.license?.plan,
            user_limit: parseInt(showEdit.license?.user_limit, 10),
            expires_at: showEdit.license?.expires_at,
          }),
        }),
      ]);
      if (!tenantRes.ok) {
        const data = await tenantRes.json();
        showNotification(
          data.detail || "Failed to update module access",
          "error",
        );
        return;
      }
      if (!licenseRes.ok) {
        const data = await licenseRes.json();
        showNotification(data.detail || "Failed to update license", "error");
        return;
      }
      showNotification("Tenant updated successfully");
      setShowEdit(null);
      fetchTenants();
    } catch {
      showNotification("Network error", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleResetAdminPassword = async () => {
    if (!showEdit) return;
    if (!adminResetPassword || adminResetPassword.length < 8) {
      showNotification("Password must be at least 8 characters", "error");
      return;
    }
    setResettingAdminPassword(true);
    try {
      const res = await apiFetch(
        `/admin/tenants/${showEdit.id}/reset-admin-password`,
        {
          method: "POST",
          body: JSON.stringify({ password: adminResetPassword }),
        },
      );
      const data = await res.json();
      if (!res.ok) {
        showNotification(
          data.detail || "Failed to reset administrator password",
          "error",
        );
        return;
      }
      showNotification(data.message || "Administrator password reset");
      setAdminResetPassword("");
    } catch {
      showNotification("Network error", "error");
    } finally {
      setResettingAdminPassword(false);
    }
  };

  // ── Toggle active / inactive ──────────────────────────────────────────
  const toggleStatus = async (ta) => {
    const newStatus = ta.status === "active" ? "inactive" : "active";
    try {
      const res = await apiFetch(`/admin/tenants/${ta.id}`, {
        method: "PATCH",
        body: JSON.stringify({ status: newStatus }),
      });
      if (!res.ok) {
        showNotification("Failed to update status", "error");
        return;
      }
      showNotification(
        `Tenant ${newStatus === "active" ? "activated" : "deactivated"}`,
      );
      fetchTenants();
    } catch {
      showNotification("Network error", "error");
    }
  };

  // ── Delete tenant ─────────────────────────────────────────────────────
  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const res = await apiFetch(`/admin/tenants/${deleteTarget.id}`, {
        method: "DELETE",
      });
      if (!res.ok) {
        const data = await res.json();
        showNotification(data.detail || "Failed to delete tenant", "error");
        return;
      }
      showNotification(
        `Tenant "${deleteTarget.company_name}" and its database permanently deleted`,
      );
      setDeleteTarget(null);
      fetchTenants();
    } catch {
      showNotification("Network error", "error");
    } finally {
      setDeleting(false);
    }
  };

  // ── Stats from local data (fast, no extra request) ────────────────────
  const activeCnt = tenants.filter((t) => t.status === "active").length;
  const inactiveCnt = tenants.filter((t) => t.status !== "active").length;

  return (
    <div className="space-y-6 animate-fade-in">
      {/* HEADER */}
      <div
        data-tour-id="tour-tenants-header"
        className="flex flex-col sm:flex-row sm:items-center justify-between gap-4"
      >
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center shadow-lg">
            <Building2 size={20} className="text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Tenant Admins</h1>
            <p className="text-sm text-slate-500 mt-0.5">
              Manage organizations and their licensing
            </p>
          </div>
        </div>
        <Button
          data-tour-id="tour-add-tenant"
          leftIcon={<Plus size={16} />}
          onClick={() => setShowCreate(true)}
        >
          Create Tenant Admin
        </Button>
      </div>

      {/* STATS */}
      <div
        data-tour-id="tour-tenants-stats"
        className="grid grid-cols-1 sm:grid-cols-3 gap-3"
      >
        <DashboardStatCard
          label="Total Tenants"
          value={total}
          sub={`${activeCnt} active`}
          icon={Building2}
          iconClass="from-indigo-500 to-violet-600"
          loading={loading}
        />
        <DashboardStatCard
          label="Active"
          value={activeCnt}
          sub="currently active"
          icon={ToggleRight}
          iconClass="from-emerald-400 to-teal-600"
          loading={loading}
        />
        <DashboardStatCard
          label="Inactive"
          value={inactiveCnt}
          sub="deactivated tenants"
          icon={ToggleLeft}
          iconClass="from-amber-400 to-orange-500"
          loading={loading}
        />
      </div>

      {/* TABLE CARD */}
      <Card padding={false}>
        <div data-tour-id="tour-tenants-table">
          {/* Search bar */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 px-5 py-4 border-b border-slate-100">
            <div className="relative flex-1 max-w-sm">
              <Search
                size={15}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
              />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search tenants…"
                className="w-full pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all"
              />
            </div>
          </div>

          {/* Tenant rows */}
          <div className="divide-y divide-slate-50">
            {loading && (
              <div className="py-16 text-center text-slate-400 text-sm">
                Loading…
              </div>
            )}
            {!loading && tenants.length === 0 && (
              <div className="py-16 text-center text-slate-400 text-sm">
                No tenants found
              </div>
            )}
            {!loading &&
              tenants.map((ta) => {
                const lic = ta.license;
                const used = lic?.users_created ?? 0;
                const limit = lic?.user_limit ?? 1;
                const pct = Math.round((used / limit) * 100);
                const isWarn = pct > 80;

                return (
                  <div
                    key={ta.id}
                    className="px-5 py-4 hover:bg-slate-50/60 transition-colors"
                  >
                    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                      {/* Left – identity */}
                      <div className="flex items-start gap-4">
                        <div className="w-11 h-11 rounded-xl bg-primary-100 flex items-center justify-center text-primary-700 font-bold text-base flex-shrink-0">
                          {ta.company_name?.[0] ?? "?"}
                        </div>
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-semibold text-slate-800">
                              {ta.company_name}
                            </span>
                            <Badge
                              variant={
                                ta.status === "active" ? "success" : "inactive"
                              }
                            >
                              {ta.status}
                            </Badge>
                            {lic && (
                              <Badge variant="primary" size="xs">
                                {lic.plan}
                              </Badge>
                            )}
                          </div>
                          <div className="text-xs text-slate-500 mt-0.5">
                            {ta.contact_email}
                          </div>
                          {ta.domain_name && (
                            <div className="text-[11px] text-slate-500 mt-0.5">
                              {ta.domain_name}
                            </div>
                          )}
                          {ta.domain_tenant_id && (
                            <div className="text-[11px] text-slate-500 font-mono mt-0.5 break-all">
                              Tenant ID: {ta.domain_tenant_id}
                            </div>
                          )}
                          {(ta.modules || []).length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-1.5">
                              {ta.modules.map((mod) => (
                                <Badge key={mod} variant="default" size="xs">
                                  {moduleLabel(mod)}
                                </Badge>
                              ))}
                            </div>
                          )}
                          <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                            {ta.organization_id != null && (
                              <span className="text-[11px] text-slate-500">
                                Org ID:{" "}
                                <span className="font-mono font-semibold text-slate-700">
                                  {ta.organization_id}
                                </span>
                              </span>
                            )}
                            <div className="flex items-center gap-1.5">
                              <Database size={11} className="text-slate-300" />
                              <span className="text-[11px] text-slate-400 font-mono">
                                {ta.db_name}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Right – license + actions */}
                      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 lg:gap-6">
                        {lic && (
                          <div className="min-w-[150px]">
                            <div className="flex justify-between text-xs mb-1.5">
                              <span className="text-slate-500 flex items-center gap-1">
                                <Users size={11} /> Users
                              </span>
                              <span className="font-medium text-slate-700">
                                {used} / {limit}
                              </span>
                            </div>
                            <ProgressBar
                              value={used}
                              max={limit}
                              color={isWarn ? "amber" : "primary"}
                            />
                            <p className="text-[10px] text-slate-400 mt-1">
                              Expires {lic.expires_at}
                            </p>
                          </div>
                        )}

                        <div className="flex items-center gap-2">
                          <Button
                            variant="secondary"
                            size="xs"
                            leftIcon={<Edit2 size={12} />}
                            onClick={() => openEditTenant(ta)}
                          >
                            Edit
                          </Button>
                          <button
                            onClick={() => toggleStatus(ta)}
                            title={
                              ta.status === "active"
                                ? "Deactivate tenant"
                                : "Activate tenant"
                            }
                            className={`p-1.5 rounded-lg transition-colors ${
                              ta.status === "active"
                                ? "text-emerald-600 hover:bg-emerald-50"
                                : "text-slate-400 hover:bg-slate-100"
                            }`}
                          >
                            {ta.status === "active" ? (
                              <ToggleRight size={20} />
                            ) : (
                              <ToggleLeft size={20} />
                            )}
                          </button>
                          <button
                            onClick={() => setDeleteTarget(ta)}
                            title="Delete tenant"
                            className="p-1.5 rounded-lg text-rose-400 hover:bg-rose-50 hover:text-rose-600 transition-colors"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
          </div>
          {!loading && total > 0 && (
            <div className="px-5 py-4 border-t border-slate-100">
              <Pagination
                page={page}
                pages={pages}
                total={total}
                pageSize={20}
                onPageChange={setPage}
                itemLabel="tenants"
              />
            </div>
          )}
        </div>
      </Card>
      <Modal
        open={showCreate}
        onClose={() => {
          setShowCreate(false);
          setErrors({});
          setForm(EMPTY_FORM);
        }}
        title="Create New Tenant"
        size="lg"
        align="top"
      >
        <FormErrorsSummary errors={errors} />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
          <Input
            label="Company Name"
            placeholder="Acme Technologies"
            value={form.company_name}
            onChange={(e) =>
              setForm((f) => ({ ...f, company_name: e.target.value }))
            }
            error={errors.company_name}
          />
          <Input
            label="Domain Name"
            placeholder="acme.com"
            hint="Primary organization domain"
            value={form.domain_name}
            onChange={(e) => {
              const domain_name = e.target.value;
              setForm((f) => ({ ...f, domain_name }));
              const domainErr = adminEmailDomainError(form.admin_email, domain_name);
              setErrors((prev) => {
                const next = { ...prev };
                if (domainErr) next.admin_email = domainErr;
                else if (next.admin_email?.includes("tenant domain")) delete next.admin_email;
                return next;
              });
            }}
            error={errors.domain_name}
          />
          <Input
            label="Organization ID"
            type="number"
            min="1"
            placeholder="e.g. 1001"
            hint="Used by DSPM, DSAR and external integrations"
            value={form.organization_id}
            onChange={(e) =>
              setForm((f) => ({ ...f, organization_id: e.target.value }))
            }
            error={errors.organization_id}
          />
          <Input
            label="Contact Email"
            type="email"
            placeholder="contact@company.com"
            value={form.contact_email}
            onChange={(e) =>
              setForm((f) => ({ ...f, contact_email: e.target.value }))
            }
            error={errors.contact_email}
          />
          <GeoMultiSelect
            selections={form.selections}
            onChange={(selections) => setForm((f) => ({
              ...f,
              selections,
              region: selections[0]?.region || DEFAULT_GEO.region,
              country: selections[0]?.country || DEFAULT_GEO.country,
            }))}
            error={errors.country}
          />
          <Input
            label="Admin Full Name"
            placeholder="John Smith"
            value={form.admin_full_name}
            onChange={(e) =>
              setForm((f) => ({ ...f, admin_full_name: e.target.value }))
            }
            error={errors.admin_full_name}
          />
          <Input
            label="Admin Email"
            type="email"
            placeholder="admin@company.com"
            value={form.admin_email}
            onChange={(e) => {
              const admin_email = e.target.value;
              setForm((f) => ({ ...f, admin_email }));
              const domainErr = adminEmailDomainError(
                admin_email,
                form.domain_name,
              );
              setErrors((prev) => {
                const next = { ...prev };
                if (domainErr) next.admin_email = domainErr;
                else if (next.admin_email?.includes("tenant domain")) delete next.admin_email;
                return next;
              });
            }}
            onBlur={() => {
              const domainErr = adminEmailDomainError(
                form.admin_email,
                form.domain_name,
              );
              if (domainErr) {
                setErrors((prev) => ({ ...prev, admin_email: domainErr }));
              }
            }}
            error={errors.admin_email}
            hint={
              form.domain_name.trim()
                ? `Must use @${form.domain_name.trim().toLowerCase()}`
                : undefined
            }
          />
          <Input
            label="Admin Password"
            type="password"
            placeholder="Min. 8 characters"
            value={form.admin_password}
            onChange={(e) =>
              setForm((f) => ({ ...f, admin_password: e.target.value }))
            }
            error={errors.admin_password}
          />
        </div>

        {/* Module access */}
        <div className="mt-4 p-4 bg-slate-50 rounded-xl border border-slate-200 space-y-3">
          <p className="text-sm font-semibold text-slate-800">Module Access</p>
          <p className="text-xs text-slate-500 -mt-1">
            Select which modules this tenant can access.
          </p>
          <ModulePicker
            value={form.modules}
            onChange={(modules) => setForm((f) => ({ ...f, modules }))}
          />
        </div>

        {/* License section */}
        <div className="mt-4 p-4 bg-primary-50 rounded-xl border border-primary-100 space-y-3">
          <div className="flex items-center gap-2 mb-1">
            <Package size={15} className="text-primary-600" />
            <span className="text-sm font-semibold text-primary-800">
              License Configuration
            </span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Input
              label="User Limit"
              type="number"
              min="1"
              max="10000"
              placeholder="25"
              value={form.user_limit}
              onChange={(e) =>
                setForm((f) => ({ ...f, user_limit: e.target.value }))
              }
              error={errors.user_limit}
            />
            <Select
              label="Plan"
              value={form.license_plan}
              onChange={(e) => handlePlanChange(e.target.value)}
            >
              {PLANS.map((p) => (
                <option key={p.value} value={p.value}>
                  {p.label}
                </option>
              ))}
            </Select>
            <Input
              label="Expiry Date"
              type="date"
              value={form.license_expires_at}
              onChange={(e) =>
                setForm((f) => ({ ...f, license_expires_at: e.target.value }))
              }
            />
          </div>
        </div>

        <div className="p-3 bg-amber-50 rounded-xl border border-amber-100 mt-3">
          <p className="text-xs text-amber-700">
            <span className="font-semibold">Database:</span> A dedicated
            isolated PostgreSQL database will be automatically provisioned for
            this tenant.
          </p>
        </div>

        <div className="flex gap-3 mt-5">
          <Button
            variant="secondary"
            onClick={() => {
              setShowCreate(false);
              setErrors({});
              setForm(EMPTY_FORM);
            }}
            className="flex-1"
          >
            Cancel
          </Button>
          <Button onClick={handleCreate} loading={saving} className="flex-1">
            Create Tenant
          </Button>
        </div>
      </Modal>

      {/* ── EDIT TENANT MODAL ─────────────────────────────────────────────── */}
      {showEdit && (
        <Modal
          open={!!showEdit}
          onClose={() => {
            setShowEdit(null);
            setAdminResetPassword("");
          }}
          title={`Edit Tenant: ${showEdit.company_name}`}
          size="lg"
          align="top"
        >
          <div className="space-y-4">
            <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 space-y-3">
              <p className="text-sm font-semibold text-slate-800">
                Organization Details
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Input
                  label="Company Name"
                  value={showEdit.company_name || ""}
                  onChange={(e) =>
                    setShowEdit((t) => ({ ...t, company_name: e.target.value }))
                  }
                />
                <Input
                  label="Contact Email"
                  type="email"
                  value={showEdit.contact_email || ""}
                  onChange={(e) =>
                    setShowEdit((t) => ({ ...t, contact_email: e.target.value }))
                  }
                />
                <GeoMultiSelect
                  selections={showEdit.selections || [resolveGeo(showEdit.region, showEdit.country)]}
                  onChange={(selections) => setShowEdit((t) => ({
                    ...t,
                    selections,
                    region: selections[0]?.region || DEFAULT_GEO.region,
                    country: selections[0]?.country || DEFAULT_GEO.country,
                  }))}
                />
                <Input
                  label="Admin Full Name"
                  value={showEdit.admin_full_name || "—"}
                  disabled
                  hint="Set at tenant creation and cannot be changed"
                />
                <Input
                  label="Admin Email"
                  type="email"
                  value={showEdit.admin_email || "—"}
                  disabled
                  hint="Set at tenant creation and cannot be changed"
                />
                <Input
                  label="Organization ID"
                  value={
                    showEdit.organization_id != null
                      ? String(showEdit.organization_id)
                      : "—"
                  }
                  disabled
                  hint="Set at tenant creation and cannot be changed"
                />
                <Input
                  label="Domain Name"
                  value={showEdit.domain_name || "—"}
                  disabled
                />
              </div>
              <Input
                label="Notes"
                value={showEdit.notes || ""}
                onChange={(e) =>
                  setShowEdit((t) => ({ ...t, notes: e.target.value }))
                }
                placeholder="Internal notes (optional)"
              />
            </div>

            <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 space-y-3">
              <p className="text-sm font-semibold text-slate-800">
                Module Access
              </p>
              <ModulePicker
                value={showEdit.modules || []}
                onChange={(modules) => setShowEdit((t) => ({ ...t, modules }))}
              />
            </div>

            <div className="p-4 bg-primary-50 rounded-xl border border-primary-100 space-y-3">
              <div className="flex items-center gap-2">
                <Package size={15} className="text-primary-600" />
                <span className="text-sm font-semibold text-primary-800">
                  License Configuration
                </span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <Input
                  label="User Limit"
                  type="number"
                  value={showEdit.license?.user_limit ?? ""}
                  onChange={(e) =>
                    setShowEdit((t) => ({
                      ...t,
                      license: {
                        ...t.license,
                        user_limit: parseInt(e.target.value, 10),
                      },
                    }))
                  }
                />
                <Select
                  label="Plan"
                  value={showEdit.license?.plan ?? "starter"}
                  onChange={(e) =>
                    setShowEdit((t) => ({
                      ...t,
                      license: { ...t.license, plan: e.target.value },
                    }))
                  }
                >
                  {PLANS.map((p) => (
                    <option key={p.value} value={p.value}>
                      {p.label}
                    </option>
                  ))}
                </Select>
                <Input
                  label="Expiry Date"
                  type="date"
                  value={showEdit.license?.expires_at ?? ""}
                  onChange={(e) =>
                    setShowEdit((t) => ({
                      ...t,
                      license: { ...t.license, expires_at: e.target.value },
                    }))
                  }
                />
              </div>
            </div>

            <div className="p-4 bg-violet-50 rounded-xl border border-violet-100 space-y-3">
              <div className="flex items-center gap-2">
                <Key size={15} className="text-violet-600" />
                <span className="text-sm font-semibold text-violet-800">
                  Reset Tenant Administrator Password
                </span>
              </div>
              <Input
                label="New Administrator Password"
                type="password"
                placeholder="Minimum 8 characters"
                value={adminResetPassword}
                onChange={(e) => setAdminResetPassword(e.target.value)}
              />
              <p className="text-xs text-violet-700">
                Sends a separate password reset email to the tenant administrator and contact email.
              </p>
              <Button
                variant="secondary"
                onClick={handleResetAdminPassword}
                loading={resettingAdminPassword}
                leftIcon={<Key size={14} />}
                disabled={!adminResetPassword}
              >
                Reset Administrator Password
              </Button>
            </div>
          </div>
          <div className="flex gap-3 mt-5">
            <Button
              variant="secondary"
              onClick={() => {
                setShowEdit(null);
                setAdminResetPassword("");
              }}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveEdit}
              loading={saving}
              className="flex-1"
            >
              Save Changes
            </Button>
          </div>
        </Modal>
      )}

      {/* ── DELETE CONFIRMATION MODAL ──────────────────────────────────────── */}
      {deleteTarget && (
        <Modal
          open={!!deleteTarget}
          onClose={() => !deleting && setDeleteTarget(null)}
          title="Delete Tenant"
          size="sm"
        >
          <div className="flex flex-col items-center text-center gap-4 py-2">
            <div className="w-14 h-14 rounded-full bg-rose-50 flex items-center justify-center flex-shrink-0">
              <AlertTriangle size={28} className="text-rose-500" />
            </div>
            <div>
              <p className="text-base font-semibold text-slate-800">
                Delete &ldquo;{deleteTarget.company_name}&rdquo;?
              </p>
              <p className="text-sm text-slate-500 mt-1.5 leading-relaxed">
                This will permanently delete the tenant, remove its registry
                record, and drop the tenant database. This action cannot be
                undone. Use the status toggle to deactivate without deleting.
              </p>
            </div>
            <div className="w-full p-3 rounded-lg bg-rose-50 border border-rose-100 text-xs text-rose-800 text-left space-y-1.5">
              <p>
                <span className="font-semibold">Organisation:</span>{" "}
                {deleteTarget.company_name}
              </p>
              <p>
                <span className="font-semibold">Domain:</span>{" "}
                {deleteTarget.domain_name || "—"}
              </p>
              <p>
                <span className="font-semibold">Database:</span>{" "}
                <span className="font-mono">{deleteTarget.db_name || "—"}</span>
              </p>
              <p>
                <span className="font-semibold">Contact:</span>{" "}
                {deleteTarget.contact_email || "—"}
              </p>
            </div>
          </div>
          <div className="flex gap-3 mt-5">
            <Button
              variant="secondary"
              onClick={() => setDeleteTarget(null)}
              className="flex-1"
              disabled={deleting}
            >
              Cancel
            </Button>
            <Button
              onClick={handleDelete}
              loading={deleting}
              className="flex-1 !bg-rose-600 hover:!bg-rose-700 !text-white !border-rose-600"
            >
              Yes, Delete Tenant
            </Button>
          </div>
        </Modal>
      )}
    </div>
  );
}
